import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static get targets() {
    return [
      "input",
      "dataCount",
      "tagTemplate",
      "includeYears",
      "excludeYears",
      "fromYear",
      "fromYearError",
      "toYear",
      "toYearError",
      "tag",
      "tagsList",
    ]
  }

  static get values() {
    return { name: String, fromYear: Number, toYear: Number }
  }

  connect() {
    Array.from(this.includeYearsTarget.options).forEach((option) => {
      if (option.selected) {
        this._appendTagToElementChildren(option.value)
      }
    })

    Array.from(this.excludeYearsTarget.options).forEach((option) => {
      if (option.selected) {
        this._appendTagToElementChildren(option.value, false)
      }
    })

    this._refreshDataCount()
  }

  updateRange(event) {
    this._clearYears()

    let fromYear = this._fromYear()
    let toYear = this._toYear()

    this.toYearTarget.classList.remove("error")
    this.toYearErrorTarget.classList.add("d-none")
    this.fromYearTarget.classList.remove("error")
    this.fromYearErrorTarget.classList.add("d-none")

    if (toYear !== 0 && fromYear > toYear) {
      if (event.target === this.toYearTarget) {
        this.toYearTarget.classList.add("error")
        this.toYearErrorTarget.classList.remove("d-none")
      } else {
        this.fromYearTarget.classList.add("error")
        this.fromYearErrorTarget.classList.remove("d-none")
      }

      return
    }

    if (fromYear === this.fromYearValue && toYear === this.toYearValue) {
      return
    }

    this.fromYearValue = fromYear
    this.toYearValue = toYear

    this._dispatchFilterEvent()
    this._refreshDataCount()
  }

  clear() {
    this._clearRange()
    this._clearYears()
    this._refreshDataCount()
    this._dispatchFilterEvent()
  }

  addTag(event) {
    event.preventDefault()
    this._clearRange()

    let value = this.inputTarget.value
    let sanitizedValue = value.replace(/[^\d]/g, "")
    if (sanitizedValue.trim() === "") {
      return
    }

    this._appendTagToElementChildren(sanitizedValue)
    this._addTagToSelect(sanitizedValue, true)
    this._refreshDataCount()
    this._dispatchFilterEvent()
    this.inputTarget.value = ""
  }

  changeTag({ detail: { tagValue, included } }) {
    this._removeTagFromSelect(tagValue, !included)
    this._addTagToSelect(tagValue, included)
    this._refreshDataCount()
    this._dispatchFilterEvent()
  }

  removeTag({ detail: { tagValue, included } }) {
    this._removeTag(tagValue, included)
    this._refreshDataCount()
    this._dispatchFilterEvent()
  }

  _clearRange() {
    this.fromYearValue = ""
    this.toYearValue = ""
    this.fromYearTarget.value = ""
    this.toYearTarget.value = ""
  }

  _clearYears() {
    Array.from(this.includeYearsTarget.selectedOptions).forEach((option) => {
      const tagValue = option.value
      this._removeTag(tagValue, true)
    })

    Array.from(this.excludeYearsTarget.selectedOptions).forEach((option) => {
      const tagValue = option.value
      this._removeTag(tagValue, false)
    })
  }

  _fromYear() {
    return this._getYear(this.fromYearTarget)
  }

  _toYear() {
    return this._getYear(this.toYearTarget)
  }

  _getYear(input) {
    let year = input.value.length > 3 ? input.value : "0"

    year = parseInt(year)

    if (isNaN(year)) {
      year = 0
    }

    return year
  }

  _addTagToSelect(value, included) {
    let option = new Option(value, value, true, true)
    let selectElement = included ? this.includeYearsTarget : this.excludeYearsTarget
    selectElement.options.add(option)
  }

  _appendTagToElementChildren(value, included = true) {
    let tagHtml = this.tagTemplateTarget.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, value)
    tagHtml = tagHtml.replace(/%VALUE%/g, value)
    tagHtml = tagHtml.replace(/%INCLUDED%/g, included)
    let tagNode = document.createElement("div")
    tagNode.innerHTML = tagHtml

    this.tagsListTarget.appendChild(tagNode.firstElementChild)
  }

  _removeTagFromSelect(tagValue, included) {
    const selectElement = included ? this.includeYearsTarget : this.excludeYearsTarget
    const index = Array.from(selectElement.options).findIndex((option) => {
      return option.value === tagValue
    })
    selectElement.options.remove(index)
  }

  _removeTag(tagValue, included) {
    this._removeTagFromSelect(tagValue, included)
    this.tagTargets.forEach((tag) => {
      if (tag.dataset.value === tagValue) {
        tag.remove()
      }
    })
  }

  // Dispatch the filter event, this can be used with other elements to apply the filters.
  _dispatchFilterEvent() {
    let fromYear = this.fromYearValue
    let toYear = this.toYearValue

    if (fromYear === 0 && toYear === 0) {
      fromYear = ""
      toYear = ""
    }

    this.dispatch("filters", {
      detail: {
        filters: [
          { name: `${this.nameValue}_from`, values: fromYear },
          { name: `${this.nameValue}_to`, values: toYear },
          { name: `${this.nameValue}_included`, values: this._selectedValues(this.includeYearsTarget) },
          { name: `${this.nameValue}_excluded`, values: this._selectedValues(this.excludeYearsTarget) },
        ],
      },
    })
  }

  // Utility method to get all values from a select element.
  _selectedValues(selectElement) {
    return Array.from(selectElement.selectedOptions).map((option) => option.value)
  }

  // Refresh the data-count attribute
  _refreshDataCount() {
    if (!this.hasDataCountTarget) {
      return
    }

    if (this.fromYearValue !== 0 || this.toYearValue !== 0) {
      this.dataCountTarget.dataset.count = (this.fromYearTarget.value !== "") + (this.toYearTarget.value !== "")
      return
    }

    let selectedOptions =
      this.includeYearsTarget.selectedOptions.length + this.excludeYearsTarget.selectedOptions.length

    if (selectedOptions > 0) {
      this.dataCountTarget.dataset.count = selectedOptions
    } else {
      delete this.dataCountTarget.dataset.count
    }
  }
}
